import graphApiSlice from 'src/shared/state/api-slices/graphApiSlice';

import {
    setPortfolioObjective,
    portfolioOverviewQuery,
    PortfolioOverviewQueryResult,
    PortfolioOverviewData,
    habitatIndicatorsQuery,
    HabitatIndicatorsQueryResult,
    HabitatIndicatorsData,
    speciesSummaryCardsQuery,
    SpeciesSummaryCardsQueryResult,
    SpeciesSummaryCardsData,
    portfolioFiltersNewQuery,
    PortfolioFiltersNewQueryResult,
} from './portfolioQuery';

type PortfolioQueryParams = {
    customerId: string;
    filters?: { name: string; values: string[] }[];
};

type SetPortfolioObjectiveParams = {
    customerId: string;
    objective: string;
};

const PortfolioGraphSlice = graphApiSlice.injectEndpoints({
    endpoints: builder => ({
        setPortfolioObjective: builder.mutation<boolean, SetPortfolioObjectiveParams>({
            query: params => ({
                body: setPortfolioObjective,
                variables: params,
            }),
            async onQueryStarted(arg, api) {
                const { queryFulfilled, dispatch } = api;

                const patchPortfolio = dispatch(
                    PortfolioGraphSlice.util.updateQueryData(
                        'portfolioOverview',
                        { customerId: arg.customerId },
                        (draft: PortfolioOverviewData) => {
                            if (draft) {
                                draft.objective = arg.objective;
                            }
                        }
                    )
                );

                try {
                    await queryFulfilled;
                } catch (error) {
                    patchPortfolio.undo();
                    console.error('Error updating portfolio objective:', error);
                }
            },
        }),
        // --- Purpose-built portfolio endpoints ---

        portfolioOverview: builder.query<PortfolioOverviewData, PortfolioQueryParams>({
            query: params => ({
                body: portfolioOverviewQuery,
                variables: params,
            }),
            transformResponse: (response: PortfolioOverviewQueryResult) => response.portfolio.getPortfolioOverview,
        }),
        habitatIndicators: builder.query<HabitatIndicatorsData, PortfolioQueryParams>({
            query: params => ({
                body: habitatIndicatorsQuery,
                variables: params,
            }),
            transformResponse: (response: HabitatIndicatorsQueryResult) => response.portfolio.getHabitatIndicators,
        }),
        speciesSummaryCards: builder.query<SpeciesSummaryCardsData, PortfolioQueryParams>({
            query: params => ({
                body: speciesSummaryCardsQuery,
                variables: params,
            }),
            transformResponse: (response: SpeciesSummaryCardsQueryResult) => response.portfolio.getSpeciesSummaryCards,
        }),
        portfolioFiltersNew: builder.query<PortfolioFiltersNewQueryResult['portfolio']['getPortfolioFilters'], { customerId: string }>({
            query: params => ({
                body: portfolioFiltersNewQuery,
                variables: params,
            }),
            transformResponse: (response: PortfolioFiltersNewQueryResult) => response.portfolio.getPortfolioFilters,
        }),
    }),
});

export const {
    useSetPortfolioObjectiveMutation,
    usePortfolioOverviewQuery,
    useHabitatIndicatorsQuery,
    useSpeciesSummaryCardsQuery,
    usePortfolioFiltersNewQuery,
} = PortfolioGraphSlice;

export default PortfolioGraphSlice.reducer;
